/**
 * Match setup, then the match.
 *
 * Both /play/bot and /play/deck-test land here. They differ in defaults, not in
 * mechanics: DECK TEST opens the log and starts with the deck you last played,
 * because the point of it is watching how a build behaves.
 *
 * Nothing starts until the player presses the button, so a refresh never drops
 * them into a half-built match.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateDeck } from '@/engine';
import { getCard } from '@/data';
import { botDelay, deckCards, useDecks, useSettings } from '@/app/stores';
import { BotMatch } from '@/ui/battle/Battlefield';
import { BOT_PLAYER, LOCAL_PLAYER, type MatchConfig, type MatchMode } from '@/ui/battle/matchController';
import type { BotDifficulty } from '@/ai/types';
import { Button, Panel, useToast } from '@/ui/system';
import styles from './MatchSetupPage.module.css';

const DIFFICULTIES: { value: BotDifficulty; label: string; blurb: string }[] = [
  { value: 'easy', label: 'Easy', blurb: 'Plays its curve and attacks when it is safe.' },
  { value: 'normal', label: 'Normal', blurb: 'Does trade maths and holds removal for real threats.' },
  { value: 'hard', label: 'Hard', blurb: 'Looks ahead at combat and plans lethal.' },
];

const RANDOM = '__random__';

export interface MatchSetupPageProps {
  mode: MatchMode;
  title: string;
  subtitle: string;
}

export function MatchSetupPage({ mode, title, subtitle }: MatchSetupPageProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const settings = useSettings();
  const decks = useDecks();
  const list = decks.list();
  const playable = useMemo(() => list.filter((d) => !d.dev || settings.devMode), [list, settings.devMode]);

  const [playerDeckId, setPlayerDeckId] = useState(() => decks.lastUsedId ?? playable[0]?.id ?? '');
  const [opponentDeckId, setOpponentDeckId] = useState<string>(RANDOM);
  const [difficulty, setDifficulty] = useState<BotDifficulty>('normal');
  const [randomFirstPlayer, setRandomFirstPlayer] = useState(true);
  const [config, setConfig] = useState<MatchConfig | null>(null);

  if (config) return <BotMatch config={config} />;

  const playerDeck = playable.find((d) => d.id === playerDeckId);
  const validation = playerDeck ? validateDeck(deckCards(playerDeck.id), { getCard }) : null;

  const start = () => {
    if (!playerDeck) {
      toast.push('Pick a deck first.', 'warn');
      return;
    }
    const pool = playable.filter((d) => d.id !== playerDeck.id);
    const opponent =
      opponentDeckId === RANDOM
        ? (pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : playerDeck)
        : playable.find((d) => d.id === opponentDeckId) ?? playerDeck;

    const playerCards = deckCards(playerDeck.id);
    const opponentCards = deckCards(opponent.id);
    if (playerCards.length === 0 || opponentCards.length === 0) {
      toast.push('That deck has no cards in it yet.', 'error');
      return;
    }

    decks.setLastUsed(playerDeck.id);
    if (mode === 'deckTest') settings.set('showLog', true);

    setConfig({
      seed: Math.floor(Math.random() * 2_000_000_000),
      mode,
      playerName: 'You',
      opponentName: `${opponent.name} bot`,
      playerDeckId: playerDeck.id,
      opponentDeckId: opponent.id,
      playerDeck: playerCards,
      opponentDeck: opponentCards,
      difficulty,
      randomFirstPlayer,
      botDelayMs: botDelay(settings),
    });
  };

  return (
    <div className={styles.page}>
      <Panel
        title={title}
        subtitle={subtitle}
        actions={
          <Button variant="ghost" size="small" onClick={() => navigate('/play')}>
            Back
          </Button>
        }
      >
        <div className={styles.form}>
          <Field label="Your deck">
            <select
              className={styles.select}
              value={playerDeckId}
              onChange={(e) => setPlayerDeckId(e.target.value)}
              data-testid="player-deck"
            >
              {playable.map((deck) => (
                <option key={deck.id} value={deck.id}>
                  {deck.name}
                  {deck.builtIn ? '' : ' (yours)'}
                </option>
              ))}
            </select>
            {validation && !validation.valid ? (
              <p className={styles.warn}>
                This deck is not tournament legal: {validation.errors?.[0] ?? 'see the deck builder'}. You can
                still play it against a bot.
              </p>
            ) : null}
          </Field>

          <Field label="Opponent deck">
            <select
              className={styles.select}
              value={opponentDeckId}
              onChange={(e) => setOpponentDeckId(e.target.value)}
              data-testid="opponent-deck"
            >
              <option value={RANDOM}>Random</option>
              {playable.map((deck) => (
                <option key={deck.id} value={deck.id}>
                  {deck.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Difficulty">
            <div className={styles.choices} role="radiogroup" aria-label="Difficulty">
              {DIFFICULTIES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={difficulty === option.value}
                  className={styles.choice}
                  data-on={difficulty === option.value}
                  onClick={() => setDifficulty(option.value)}
                >
                  <span className={styles.choiceLabel}>{option.label}</span>
                  <span className={styles.choiceBlurb}>{option.blurb}</span>
                </button>
              ))}
            </div>
          </Field>

          <Field label="First player">
            <div className={styles.choices} role="radiogroup" aria-label="First player">
              <button
                type="button"
                role="radio"
                aria-checked={randomFirstPlayer}
                className={styles.choice}
                data-on={randomFirstPlayer}
                onClick={() => setRandomFirstPlayer(true)}
              >
                <span className={styles.choiceLabel}>Random</span>
                <span className={styles.choiceBlurb}>Decided by the seeded coin flip, as in a real match.</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={!randomFirstPlayer}
                className={styles.choice}
                data-on={!randomFirstPlayer}
                onClick={() => setRandomFirstPlayer(false)}
              >
                <span className={styles.choiceLabel}>You go first</span>
                <span className={styles.choiceBlurb}>Useful for testing an opening.</span>
              </button>
            </div>
          </Field>

          <Button variant="primary" size="large" block onClick={start} data-testid="start-match">
            Start match
          </Button>
        </div>
      </Panel>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </div>
  );
}

export function BotMatchPage() {
  return (
    <MatchSetupPage
      mode="bot"
      title="Play vs bot"
      subtitle="Pick your deck and who you want to beat."
    />
  );
}

export function DeckTestPage() {
  return (
    <MatchSetupPage
      mode="deckTest"
      title="Deck test"
      subtitle="The same match with the log open, for watching how a build actually plays."
    />
  );
}

export { LOCAL_PLAYER, BOT_PLAYER };
